import { useCallback, useEffect, useRef } from 'react';
import { useNavigationType } from 'react-router-dom';

/** How long Back waits for the list to grow tall enough to scroll to. */
const RESTORE_TIMEOUT_MS = 1500;

/**
 * Brings the page back to where it was when the user returns to it with
 * Back (a POP), so walking a long list location by location does not start
 * from the top every time. A fresh visit (a PUSH, from the menu or a link)
 * still opens at the top, as ScrollToTop does for every page.
 *
 * The position is saved when the user clicks inside the list (pass `save`
 * to the list's onClickCapture), not on scroll or on unmount: while the next
 * page loads, the old one is hidden, the page shrinks and the browser scrolls
 * it to the top, which a scroll listener would have saved instead.
 *
 * `ready` is when the list is on screen: before that there is nothing tall
 * enough to scroll to.
 */
export const useListScrollMemory = (key: string, ready: boolean) => {
  const navigationType = useNavigationType();
  const restored = useRef(false);

  const save = useCallback(() => {
    try {
      sessionStorage.setItem(key, String(window.scrollY));
    } catch {
      // Storage blocked: the list just opens at the top.
    }
  }, [key]);

  // A passive effect, so it runs after ScrollToTop's layout effect has put
  // the page at the top.
  useEffect(() => {
    if (!ready || restored.current) return;
    restored.current = true;
    if (navigationType !== 'POP') return;
    let saved: string | null = null;
    try {
      saved = sessionStorage.getItem(key);
    } catch {
      return;
    }
    const top = saved ? parseInt(saved, 10) : NaN;
    if (Number.isNaN(top) || top <= 0) return;

    // The rows can take a few frames to reach their full height; until then
    // the browser would cut the scroll short.
    const until = Date.now() + RESTORE_TIMEOUT_MS;
    let frame = 0;
    const scroll = () => {
      window.scrollTo({ top });
      const tallEnough =
        document.documentElement.scrollHeight >= top + window.innerHeight;
      if (!tallEnough && Date.now() < until) {
        frame = requestAnimationFrame(scroll);
      }
    };
    scroll();
    return () => cancelAnimationFrame(frame);
  }, [key, ready, navigationType]);

  return save;
};
